import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect } from "react"
import { customPalette } from "../../../../../../../../../../theme"
import { SquareBadge } from "../../../../../../../../../components/badges/SquareBadge"
import { IconPencil } from "../../../../../../../../../components/custom-icons/icon-pencil"
import { IconTrash } from "../../../../../../../../../components/custom-icons/icon-trash"
import { FormNumberInput } from "../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { NonEditableTextField } from "../../../../../../../../../components/inputs/text-input/NonEditableTextField"
import { BSPatio } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatio"
import { BSPatioForm } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatioForm"
import { levelNumberToLabel, stringToNumber } from "../../../../../../../../../core/services/helper-service"
import { BSPatioContext } from "../context/BSPatioContext"
import { BSPatioFormContext } from "../context/BSPatioFormContext"

interface IProps {
  bsPatio: BSPatio
  index: number
  displayNewBSPatioForm: boolean
}

export function BSPatioInfosCard({ bsPatio, index, displayNewBSPatioForm }: Readonly<IProps>): React.JSX.Element {
  const { setIsEditing, deleteBSPatioInBSPatioList } = useContext(BSPatioContext)

  const {
    errors,
    setErrors,
    selectedBSPatioForm,
    setSelectedBSPatioForm,
    fieldValidators,
    setFieldValue,
    validateBSPatioForm,
    bsPatioDtoToForm,
    updateBSPatioFormList,
    selectPatio,
    setCurrentEditedIndex,
    isCurrentlyEditedIndex,
  } = useContext(BSPatioFormContext)

  useEffect(
    () => () => {
      setIsEditing(false)
    },
    [setIsEditing]
  )

  const handleEditPatio: () => void = useCallback(() => {
    setSelectedBSPatioForm(bsPatioDtoToForm(bsPatio))
    setCurrentEditedIndex(index)
    setIsEditing(true)
  }, [setSelectedBSPatioForm, bsPatioDtoToForm, bsPatio, setCurrentEditedIndex, index, setIsEditing])

  const handleCancelUpdate: () => void = useCallback(() => {
    selectPatio(undefined)
    setCurrentEditedIndex(undefined)
    setIsEditing(false)
    setErrors({})
  }, [selectPatio, setIsEditing, setErrors, setCurrentEditedIndex])

  const handleConfirmUpdates: () => void = useCallback(() => {
    if (selectedBSPatioForm && validateBSPatioForm(selectedBSPatioForm)) {
      updateBSPatioFormList(selectedBSPatioForm, index)
      selectPatio(undefined)
      setCurrentEditedIndex(undefined)
      setIsEditing(false)
    }
  }, [
    selectedBSPatioForm,
    validateBSPatioForm,
    updateBSPatioFormList,
    index,
    selectPatio,
    setIsEditing,
    setCurrentEditedIndex,
  ])

  const handleFieldChange: (field: keyof BSPatioForm, value: number) => void = useCallback(
    (field, value) => {
      setFieldValue(field, value)
      fieldValidators[field]?.(field, value)
    },
    [fieldValidators, setFieldValue]
  )

  const handleDeletePatio: () => void = useCallback(() => {
    deleteBSPatioInBSPatioList(index)
  }, [deleteBSPatioInBSPatioList, index])

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-around" }}>
      <Paper elevation={2} sx={{ borderRadius: 4, mb: 2, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontSize="14px" sx={{ color: customPalette.mediumGreen3 }} mb={1}>{`PATIO ${index + 1}`}</Typography>
        </Box>
        <Box display="flex" gap={2}>
          {isCurrentlyEditedIndex(index) ? (
            <>
              <FormNumberInput
                fieldName="surface"
                label="Surface (m²)"
                value={bsPatio.surface}
                handleChange={(value) => handleFieldChange("surface", stringToNumber(value))}
                errors={errors}
              />
              <Tooltip title="Le 0 correspond au rez-de-chaussée." placement="top" arrow>
                <span style={{ width: "100%" }}>
                  <FormNumberInput
                    fieldName="startingLevelNumber"
                    label="Niveau de départ"
                    value={bsPatio.startingLevelNumber}
                    handleChange={(value) => handleFieldChange("startingLevelNumber", stringToNumber(value))}
                    errors={errors}
                  />
                </span>
              </Tooltip>
              <FormNumberInput
                fieldName="impactedFloor"
                label="Nombre d'étage impactés"
                value={bsPatio.impactedFloor}
                handleChange={(value) => handleFieldChange("impactedFloor", stringToNumber(value))}
                errors={errors}
              />
            </>
          ) : (
            <>
              <NonEditableTextField label="Surface" value={bsPatio?.surface} />
              <NonEditableTextField label="Niveau de départ" value={levelNumberToLabel(bsPatio.startingLevelNumber)} />
              <NonEditableTextField label="Nombre d'étages impactés" value={bsPatio?.impactedFloor} />
            </>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 2, mx: 1 }}>
        {isCurrentlyEditedIndex(index) ? (
          <>
            <IconButton
              color="error"
              onClick={handleCancelUpdate}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255, 46, 46, 0.1)",
                },
              }}>
              <CloseIcon />
            </IconButton>
            <IconButton
              color="success"
              onClick={handleConfirmUpdates}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                },
              }}>
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          <>
            <SquareBadge
              onClick={handleEditPatio}
              icon={
                <IconPencil sx={{ color: displayNewBSPatioForm ? customPalette.lightGray2 : customPalette.lightBlue2 }} />
              }
              backgroundColor={displayNewBSPatioForm ? customPalette.lightGray5 : customPalette.lightGray7}
              cursor={displayNewBSPatioForm ? "default" : "pointer"}
              disabled={displayNewBSPatioForm}
            />
            <SquareBadge
              onClick={handleDeletePatio}
              icon={
                <IconTrash sx={{ color: displayNewBSPatioForm ? customPalette.lightGray2 : customPalette.lightBlue2 }} />
              }
              backgroundColor={displayNewBSPatioForm ? customPalette.lightGray5 : customPalette.lightGray7}
              cursor={displayNewBSPatioForm ? "default" : "pointer"}
              disabled={displayNewBSPatioForm}
            />
          </>
        )}
      </Box>
    </Box>
  )
}
